import { useCallback, useEffect, useRef, useState } from 'react';
import Tilt from 'react-parallax-tilt';
import HeroScene from '../Three/HeroScene';
import TechConstellation from '../Three/TechConstellation';
import CaseStudy from './CaseStudy';
import haloLearn from '../../Assets/Projects/Halo_learn.png';
import editor from '../../Assets/Projects/codeEditor.png';
import spotifyClone from '../../Assets/Projects/spotify_clone.png';
import adminDashboard from '../../Assets/Projects/admin_dashboard.png';
import tensorWave from '../../Assets/Projects/tensorwave.png';

/* ── Gradient mesh canvas ── */
function initMesh(canvas) {
  const ctx = canvas.getContext('2d');
  let W, H, t = 0;
  const blobs = [
    { x: 0.15, y: 0.2,  r: 0.45, h: 220, a: 0.0012 },
    { x: 0.75, y: 0.15, r: 0.40, h: 210, a: -0.0008 },
    { x: 0.5,  y: 0.7,  r: 0.50, h: 230, a: 0.0010 },
    { x: 0.85, y: 0.65, r: 0.35, h: 215, a: -0.0014 },
  ];
  function resize() {
    W = canvas.width = window.innerWidth;
    H = canvas.height = window.innerHeight;
  }
  resize();
  window.addEventListener('resize', resize);
  let raf;
  function draw() {
    ctx.clearRect(0, 0, W, H);
    blobs.forEach(b => {
      t += b.a;
      const px = (b.x + Math.sin(t * 1.2) * 0.12) * W;
      const py = (b.y + Math.cos(t * 0.9) * 0.1) * H;
      const r = b.r * Math.min(W, H);
      const g = ctx.createRadialGradient(px, py, 0, px, py, r);
      g.addColorStop(0, `hsla(${b.h}, 80%, 55%, 0.16)`);
      g.addColorStop(1, `hsla(${b.h}, 80%, 45%, 0)`);
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(px, py, r, 0, Math.PI * 2);
      ctx.fill();
    });
    raf = requestAnimationFrame(draw);
  }
  draw();
  return () => {
    cancelAnimationFrame(raf);
    window.removeEventListener('resize', resize);
  };
}

/* ── Scroll reveal ── */
function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.reveal');
    const io = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.12 }
    );
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);
}

/* ── DATA ── */
const PROJECTS = [
  {
    title: 'Halo Learn — GCU LMS',
    tag: 'Full Stack',
    year: '2022–2025',
    desc: 'Mission-critical educational platform serving 100,000+ active users at Grand Canyon University. Engineered a GraphQL integration layer connecting 10+ microservices with real-time course delivery, assignment workflows, and grade synchronization. Reduced API response times by 40%.',
    stack: ['React.js', 'GraphQL', 'Apollo', 'Node.js', 'Jest', 'Microservices'],
    img: haloLearn,
    demo: 'https://testdrive.gcu.edu/',
    featured: true,
    study: {
      role: 'Full Stack Engineer · EXL for Grand Canyon University',
      timeline: '2022 — 2025',
      problem: 'GCU\'s LMS served 100,000+ students and faculty, but course, assignment, and grading data was spread across 10+ microservices with inconsistent contracts. The client stitched together dozens of chained REST calls per page, and the grading workflow — the most used feature by faculty — was brittle and slow to change.',
      constraints: [
        'Zero-downtime releases — a live academic calendar means no maintenance windows',
        'Service contracts owned by other teams and not ours to change quickly',
        'Accessibility and FERPA compliance on every surface',
        'Four environments (Dev / QA / UAT / Prod) with manual promotion',
      ],
      decision: 'Introduced a unified GraphQL layer (Apollo) as the single contract for the client, resolving across microservices server-side so the UI stopped caring where data lived. Paired it with React Query caching and a shared 25+ component library so three product teams shipped against one design system. Locked in quality with a Jest suite above 90% coverage and Azure Pipelines CI/CD with automated promotion between environments.',
      results: [
        { num: '100K+', label: 'active users served' },
        { num: '40%', label: 'faster API responses' },
        { num: '70%', label: 'more frequent deploys' },
        { num: '30%', label: 'fewer post-release bugs' },
      ],
      tradeoff: 'The GraphQL gateway became a single point of coupling — every schema change needed cross-team review. Worth it for client simplicity, but I\'d invest earlier in schema federation and consumer-driven contract tests.',
    },
  },
  {
    title: 'CodeHarbor — AI Code Analysis',
    tag: 'Frontend',
    year: '2025',
    desc: '2025 Award Winner for Technical Innovation. AI-native enterprise code analysis platform. Owned the frontend — interactive dependency graph visualizations, analytics dashboards, and in-browser code viewing. Delivered to US Bank and Lending Club for legacy codebase migration.',
    stack: ['React', 'TypeScript', 'Vite', 'TanStack Query', 'React Flow', 'Recharts', 'Monaco Editor', 'Radix UI', 'Tailwind CSS'],
    featured: true,
    study: {
      role: 'Frontend Lead · EXL',
      timeline: '2025',
      problem: 'Enterprise clients needed to understand and migrate massive legacy codebases, but existing tools offered static reports with no interactivity. Stakeholders could not explore dependencies, visualize code health trends, or browse code in context — making migration planning a guessing game.',
      constraints: [
        'Delivered to Fortune 500 clients (US Bank, Lending Club) with strict security requirements',
        'Interactive dependency graphs with 1000+ nodes must stay performant',
        'In-browser code viewing without leaking source to the client',
        'Frontend quality bar: TypeScript strict, Zod runtime validation, Husky pre-commit hooks',
      ],
      decision: 'Built interactive dependency graph visualizations with React Flow for node-link exploration, analytics dashboards with Recharts for code health trends, and Monaco Editor for in-browser code viewing. Established frontend quality tooling including Vitest test suites, TypeScript strict type-checking, ESLint/Prettier via Husky pre-commit hooks, and Zod runtime schema validation.',
      results: [
        { num: '2025', label: 'Technical Innovation Award' },
        { num: '2', label: 'Fortune 500 clients delivered' },
        { num: '1000+', label: 'nodes rendered smoothly' },
      ],
      tradeoff: 'React Flow\'s built-in layout algorithms struggled past ~500 nodes. We integrated dagre for auto-layout but lost some of the physics-based organic feel. For a next version I\'d explore a WebGL renderer like Cytoscape.js for the largest graphs.',
    },
  },
  {
    title: 'GPU Telemetry Dashboard',
    tag: 'Frontend',
    year: '2026',
    desc: 'High-performance, real-time GPU telemetry dashboard. A client-side simulation layer mimics a live WebSocket feed, driving metrics via a bounded random walk with a sine "workload wave" and correlated thermals. A rolling 60s store binds to React through useSyncExternalStore with granular slice selectors, so each tick re-renders only the leaves whose data changed.',
    stack: ['React', 'TypeScript', 'Vite', 'Tailwind CSS', 'Recharts', 'Radix'],
    img: tensorWave,
    gh: 'https://github.com/jrbauti19/gpu-telemetry',
    demo: 'https://gpu-telemetry.vercel.app/',
    study: {
      role: 'Solo — design, build, deploy',
      timeline: '2026',
      problem: 'Real-time dashboards usually re-render the whole tree on every tick. At 10+ updates per second across a dozen charts, a naive React implementation drops frames and the UI feels jittery — exactly the opposite of what a telemetry tool should feel like.',
      constraints: [
        'No backend — has to feel live with a client-side simulation',
        'Smooth 60fps on a laptop with 12 charts updating concurrently',
        'Type-safe end to end',
        'Shipped over a weekend',
      ],
      decision: 'Built an external rolling 60-second store and bound it to React through useSyncExternalStore with granular slice selectors, so each tick re-renders only the leaf components whose data actually changed. The simulation is a bounded random walk with a sine "workload wave" and correlated thermals, so the data tells a believable story instead of looking like noise.',
      results: [
        { num: '60fps', label: 'with 12 live charts' },
        { num: '~0', label: 'wasted re-renders per tick' },
        { num: '10/s', label: 'update cadence' },
      ],
      tradeoff: 'Recharts was fast to ship but is SVG-based; past ~20 simultaneous charts a canvas renderer like uPlot would scale much better.',
    },
  },
  {
    title: 'Spotify Web Client Clone',
    tag: 'Full Stack',
    year: '2023',
    desc: 'Full-featured Spotify clone with real-time music playback via the Spotify Playback SDK, playlist management, advanced search, and OAuth 2.0 authentication.',
    stack: ['React', 'TypeScript', 'Redux', 'Spotify API', 'OAuth 2.0'],
    img: spotifyClone,
    gh: 'https://github.com/jrbauti19/spotify-react-web-client',
    demo: 'https://spotify-react-web-client-zeta.vercel.app/',
    study: {
      role: 'Solo — full stack',
      timeline: '2023',
      problem: 'Recreate the Spotify web client faithfully — real playback, not mocked audio — including OAuth, playlist management, search, and device handoff. The goal was to prove I could match a product people use daily, pixel for pixel and interaction for interaction.',
      constraints: [
        'Spotify Playback SDK requires Premium and a strict token lifecycle',
        'Rate-limited public API',
        'Pixel parity with a UI users know intimately',
      ],
      decision: 'Redux for normalized playback and library state, a token-refresh middleware handling the OAuth 2.0 flow, and a thin adapter around the Playback SDK so the player could be swapped or mocked in tests without touching UI code.',
      results: [
        { num: 'OAuth 2.0', label: 'end-to-end auth flow' },
        { num: 'Real', label: 'playback via SDK' },
        { num: '1:1', label: 'UI parity' },
      ],
      tradeoff: 'Redux boilerplate was heavy for the scope. Today I\'d reach for React Query for server state and Zustand for the player.',
    },
  },
  {
    title: 'Editor.io',
    tag: 'Dev Tool',
    year: '2023',
    desc: 'Online code and markdown editor built with React. Supports HTML, CSS, and JS with instant preview. Markdown editor supports GFM, custom HTML tags, and auto-save via Local Storage.',
    stack: ['React.js', 'JavaScript', 'Local Storage', 'CSS'],
    img: editor,
    gh: 'https://github.com/jrbauti19/Editor.io',
    demo: 'https://code-editor-ten-brown.vercel.app/',
    study: {
      role: 'Solo — frontend',
      timeline: '2023',
      problem: 'Quick HTML/CSS/JS sandboxes and markdown scratchpads usually gate you behind an account. I wanted a zero-friction editor that opens instantly, previews live, and never loses your work.',
      constraints: [
        'No backend, no auth',
        'Instant preview without freezing the editor while typing',
        'Work must survive a refresh',
      ],
      decision: 'Sandboxed iframe preview with debounced compilation, GFM markdown with custom HTML tag support, and an autosave layer on Local Storage keyed per document.',
      results: [
        { num: '0', label: 'accounts required' },
        { num: 'Live', label: 'preview while typing' },
        { num: '100%', label: 'work persisted locally' },
      ],
      tradeoff: 'Local Storage caps around 5MB. A next version would move to IndexedDB with optional cloud sync.',
    },
  },
  {
    title: 'Tokyo Admin Dashboard',
    tag: 'UI / Design',
    year: '2023',
    desc: 'Professional React TypeScript admin dashboard with Material-UI. Features a responsive layout, reusable UI components, and advanced React patterns demonstrating TypeScript and component library architecture.',
    stack: ['React', 'TypeScript', 'Material-UI', 'styled-components'],
    img: adminDashboard,
    gh: 'https://github.com/jrbauti19/tokyo-free-white-react-admin-dashboard',
    demo: 'https://react-admin-dashboard-mu-jade.vercel.app/',
    study: {
      role: 'Solo — UI architecture',
      timeline: '2023',
      problem: 'Admin UIs are where design systems go to die. The goal was a TypeScript-first dashboard where every component is reusable, themeable, and typed — not a pile of one-off pages.',
      constraints: [
        'Strict TypeScript, no escape hatches',
        'Responsive down to mobile',
        'Extend MUI theming without fighting it',
      ],
      decision: 'Composable layout primitives, typed component API surfaces, and styled-components layered over MUI\'s theme for overrides so the design tokens live in one place.',
      results: [
        { num: '30+', label: 'typed reusable components' },
        { num: '100%', label: 'TypeScript coverage' },
      ],
      tradeoff: 'Two styling systems (MUI + styled-components) doubled the mental overhead. One is enough — I\'d pick MUI\'s sx or CSS Modules.',
    },
  },
];

const SKILLS = [
  {
    icon: '⬡',
    name: 'Frontend',
    color: '#0091ff',
    tags: ['React.js', 'Next.js', 'React Native', 'TypeScript', 'Redux', 'React Query', 'Apollo', 'Tailwind CSS', 'Vite'],
  },
  {
    icon: '◎',
    name: 'Testing & QA',
    color: '#00d4aa',
    tags: ['Playwright', 'Jest', 'Vitest', 'pytest', 'E2E Testing', 'TDD', 'Page Object Model', 'Bug Triage', 'Regression Testing'],
  },
  {
    icon: '◈',
    name: 'Backend & APIs',
    color: '#0055d4',
    tags: ['Node.js', 'Express', 'Apollo GraphQL', 'Python', 'Flask', 'SQLAlchemy', 'REST APIs'],
  },
  {
    icon: '⬢',
    name: 'Databases & Cloud',
    color: '#00c2ff',
    tags: ['PostgreSQL', 'SQL Server', 'AWS/S3', 'Azure Pipelines', 'Docker', 'CI/CD', 'Git'],
  },
  {
    icon: '✦',
    name: 'AI & Dev Tools',
    color: '#a259ff',
    tags: ['Claude Code', 'Cursor', 'Windsurf', 'Replit', 'Zod', 'React Flow', 'Recharts', 'Monaco Editor'],
  },
];

const EDUCATION = [
  {
    period: 'Aug 2015 — May 2019',
    degree: 'B.S. in Biomedical Sciences',
    school: 'Arizona State University · Tempe, AZ',
    desc: 'Four-year degree with a focus on engineering principles applied to medicine and biology.',
    honors: [
      { label: 'Cum Laude', style: 'gold' },
      { label: "Dean's List — Freshman to Senior", style: 'blue' },
    ],
  },
  {
    period: 'Jan 2021 — Jul 2021',
    degree: 'Full Stack Software Development',
    school: 'App Academy',
    desc: 'Intensive full-stack software development coding bootcamp covering modern web technologies, algorithms, and engineering best practices.',
    honors: [],
  },
];

const EXPERIENCE = [
  {
    period: 'Aug 2025 — Present',
    role: 'Software Engineer, QA Automation',
    company: 'QA Wolf · Remote',
    desc: 'Authored and maintained E2E automated test suites for 10+ client engagements using Playwright, TypeScript, and JavaScript, triaging ~15 test suites per week. Achieved 80%+ test coverage in 2-week sprints using the Page Object Model pattern. Triaged test failures across large suites, distinguishing genuine regressions from flaky tests and delivering clear bug and flake reports. Partnered directly with clients on test coverage strategy, bug revalidation, and ongoing QA needs.',
  },
  {
    period: 'Apr 2023 — Aug 2025',
    role: 'Software Engineer II',
    company: 'EXL · Phoenix, AZ',
    desc: 'Built a TypeScript-first design system in React and Next.js with 25+ reusable UI components, reducing development time by 30% across 3 product teams. Integrated React Query and custom caching layers into Next.js apps, resulting in a 30% performance boost. Implemented WCAG 2.1 AA accessibility standards across React and Next.js components. Improved state management performance using memoization and render optimization to handle 200+ synchronous component interactions. Configured CI/CD pipelines in Azure with Dockerized environments, enabling zero-downtime releases with a 70% increase in deployment frequency.',
  },
  {
    period: 'Oct 2022 — Apr 2023',
    role: 'Software Engineer',
    company: 'EXL · Phoenix, AZ',
    desc: 'Delivered a mission-critical Grading Workflow feature in React, Next.js, and TypeScript to 100,000+ active users for Grand Canyon University\'s LMS. Built a GraphQL orchestration service integrating 10+ microservices, improving resource management efficiency by 25% and API response times by 40% through query batching and reduced over-fetching. Introduced TDD practices using Jest with 90%+ code coverage, reducing post-deployment bugs by 30%.',
  },
  {
    period: 'Jun 2021 — Oct 2022',
    role: 'Software Engineering Intern',
    company: 'EXL · Phoenix, AZ',
    desc: 'Built production frontend features and UI components with responsive layouts, integrating with backend APIs across payment processing and domain search workflows. Contributed to Python/Flask backend services with SQLAlchemy and SQL Server, writing pytest unit tests to verify behavior and prevent regressions.',
  },
];

/* ── ARROW SVG ── */
const Arrow = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M2 7h10M7 2l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

/* ── PROJECT CARD ── */
function ProjectCard({ project, index, onOpen }) {
  const [hovered, setHovered] = useState(false);

  const handleClick = e => {
    if (e.target.closest('a')) return;
    onOpen(index);
  };
  const handleKey = e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpen(index);
    }
  };

  return (
    <div
      className={`project-tilt reveal${project.featured ? ' featured' : ''}`}
      style={{ transitionDelay: `${(index % 2) * 0.1}s` }}
    >
    <Tilt
      tiltMaxAngleX={5}
      tiltMaxAngleY={5}
      perspective={1200}
      scale={1.015}
      transitionSpeed={1800}
      glareEnable
      glareMaxOpacity={0.1}
      glareColor="#0091ff"
      glarePosition="all"
      glareBorderRadius="24px"
    >
    <div
      className={`project-card-pf${project.featured ? ' featured' : ''}`}
      role="button"
      tabIndex={0}
      aria-label={`Open case study: ${project.title}`}
      onClick={handleClick}
      onKeyDown={handleKey}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="project-thumb-pf" style={hovered ? { filter: 'brightness(1.1)' } : {}}>
        {project.img
          ? <img src={project.img} alt={project.title} />
          : (
            <div className="project-thumb-decor">
              <div className="project-thumb-decor-glow" />
              <div className="project-thumb-decor-content">
                <span className="project-thumb-decor-tag">{project.tag}</span>
                <span className="project-thumb-decor-title">{project.title}</span>
                <span className="project-thumb-decor-year">{project.year}</span>
                <div className="project-thumb-decor-stack">
                  {project.stack.slice(0, 4).map((s, j) => <span key={j}>{s}</span>)}
                  {project.stack.length > 4 && <span>+{project.stack.length - 4}</span>}
                </div>
              </div>
            </div>
          )
        }
      </div>
      <div className="project-info-pf">
        <div className="project-meta-pf">
          <span className="project-tag-pf">{project.tag}</span>
          <span className="project-year-pf">{project.year}</span>
        </div>
        <div className="project-title-pf">{project.title}</div>
        <div className="project-desc-pf">{project.desc}</div>
        <div className="project-stack-pf">
          {project.stack.map((s, j) => <span key={j} className="stack-item-pf">{s}</span>)}
        </div>
        <div className="project-links-row">
          {project.demo && (
            <a href={project.demo} target="_blank" rel="noreferrer" className="project-link-btn accent">
              View live <Arrow />
            </a>
          )}
          {project.gh && (
            <a href={project.gh} target="_blank" rel="noreferrer" className="project-link-btn">
              GitHub
            </a>
          )}
        </div>
        <div className="project-link-hint project-study-hint" style={{ marginTop: 8 }}>
          {project.featured ? 'Featured · ' : ''}Read case study <Arrow />
        </div>
      </div>
    </div>
    </Tilt>
    </div>
  );
}

/* ── MAIN COMPONENT ── */
function Home() {
  const canvasRef = useRef(null);
  const [hoverSkill, setHoverSkill] = useState(null);
  const [openIdx, setOpenIdx] = useState(null);
  useReveal();

  useEffect(() => {
    if (canvasRef.current) {
      return initMesh(canvasRef.current);
    }
  }, []);

  const activeSkill = hoverSkill;

  const closeStudy = useCallback(() => setOpenIdx(null), []);
  const navStudy = useCallback(dir => {
    setOpenIdx(i => (i === null ? null : (i + dir + PROJECTS.length) % PROJECTS.length));
  }, []);

  return (
    <>
      <canvas id="mesh-canvas" ref={canvasRef} />
      <HeroScene />

      {/* ── HERO ── */}
      <section style={{ position: 'relative', zIndex: 1 }}>
        <div id="hero">
          <div className="hero-label">Open to opportunities · Software Engineer · Frontend & QA Automation</div>
          <h1 className="hero-name">
            Joshua<br />
            <span className="accent-grad">Bautista</span>
          </h1>
          <p className="hero-tagline">
            Software Engineer with 5+ years spanning frontend development and QA automation —
            from GraphQL API layers serving 100K+ users to E2E test suites with Playwright.
          </p>
          <div className="hero-stats">
            <div>
              <div className="hero-stat-num">100K<span>+</span></div>
              <div className="hero-stat-label">Users served</div>
            </div>
            <div>
              <div className="hero-stat-num">40<span>%</span></div>
              <div className="hero-stat-label">Faster APIs</div>
            </div>
            <div>
              <div className="hero-stat-num">5<span>+</span></div>
              <div className="hero-stat-label">Years experience</div>
            </div>
          </div>
          <div className="hero-ctas">
            <a href="#projects" className="btn-primary-pf">
              View my work <Arrow />
            </a>
            <a href="#contact" className="btn-ghost-pf">Get in touch</a>
          </div>
          <div className="hero-scroll-hint">
            <span className="scroll-line" />
            <span>Scroll to explore</span>
            <span className="hero-3d-hint">· Move your cursor · Click to reshape</span>
          </div>
        </div>
      </section>

      {/* ── ABOUT ── */}
      <section style={{ position: 'relative', zIndex: 1 }}>
        <div id="about">
          <div className="about-grid">
            <div className="reveal">
              <div className="section-label">{"// about me"}</div>
              <h2 className="about-heading">
                Code as craft,<br /><em>shipped with care.</em>
              </h2>
              <div className="about-body">
                <p>
                  I'm Joshua — a Full Stack Software Engineer based in Phoenix, Arizona.
                  I currently work at EXL architecting scalable React and GraphQL applications
                  for Grand Canyon University, directly supporting 100,000+ students and faculty daily.
                </p>
                <p>
                  I thrive at the intersection of performance engineering and clean architecture —
                  whether that means cutting API response times by 40%, integrating 10+ microservices
                  through a unified GraphQL layer, or establishing testing standards above 85% coverage.
                </p>
                <p>
                  Open to remote-first, high-ownership roles at ambitious companies.
                  Based in Phoenix, AZ · (661) 343-8584
                </p>
              </div>
            </div>
            <div className="reveal" style={{ transitionDelay: '0.15s' }}>
              <div className="about-card">
                <div className="about-card-title">What I bring</div>
                <div className="about-values">
                  {[
                    'End-to-end ownership from DB to UI',
                    'GraphQL & microservices architecture',
                    'Obsessive attention to performance',
                    'Systems thinking before writing code',
                    'Clear communication, async-native',
                  ].map((v, i) => (
                    <div key={i} className="about-value">
                      <div className="value-dot" />
                      {v}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SKILLS ── */}
      <section id="skills" style={{ position: 'relative', zIndex: 1 }}>
        <div className="skills-inner">
          <div className="skills-top">
            <div className="reveal">
              <div className="section-label">{"// expertise"}</div>
              <h2 className="skills-heading">The full stack.</h2>
              <p className="skills-sub">
                From database schema to deployment pipeline — I work across the entire product surface.
              </p>
            </div>
            <div className="reveal" style={{ transitionDelay: '0.15s' }}>
              <div className="skill-sphere-wrap">
                <TechConstellation
                  skills={SKILLS}
                  projects={PROJECTS}
                  onHover={setHoverSkill}
                />
                <div className={`skill-sphere-readout${activeSkill ? ' active' : ''}`}>
                  {activeSkill ? (
                    <>
                      <span className="skill-sphere-dot" style={{ background: activeSkill.color }} />
                      <span className="skill-sphere-tag">{activeSkill.text}</span>
                      <span className="skill-sphere-cat">{activeSkill.cat}</span>
                    </>
                  ) : (
                    <span className="skill-sphere-hint">Drag to rotate · hover a star to see tech details</span>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="skills-categories">
            {SKILLS.map((cat, i) => (
              <div key={i} className="skill-cat reveal" style={{ transitionDelay: `${i * 0.1}s` }}>
                <div className="skill-cat-icon" style={{ background: `${cat.color}18` }}>
                  <span style={{ color: cat.color, fontFamily: 'monospace', fontSize: '20px' }}>
                    {cat.icon}
                  </span>
                </div>
                <div className="skill-cat-name">{cat.name}</div>
                <div className="skill-tags">
                  {cat.tags.map((t, j) => <span key={j} className="skill-tag">{t}</span>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PROJECTS ── */}
      <section style={{ position: 'relative', zIndex: 1 }}>
        <div id="projects">
          <div className="projects-header reveal">
            <div>
              <div className="section-label">{"// selected work"}</div>
              <h2 className="projects-heading">Things I've built.</h2>
            </div>
            <a
              href="https://github.com/jrbauti19"
              target="_blank"
              rel="noreferrer"
              style={{
                color: 'var(--muted)',
                fontSize: '14px',
                textDecoration: 'none',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              See all on GitHub
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M2 6h8M6 2l4 4-4 4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </a>
          </div>
          <div className="projects-grid">
            {PROJECTS.map((p, i) => (
              <ProjectCard
                key={i}
                project={p}
                index={i}
                onOpen={setOpenIdx}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── EXPERIENCE ── */}
      <section id="experience" style={{ position: 'relative', zIndex: 1 }}>
        <div className="exp-inner">
          <div className="reveal">
            <div className="section-label">{"// experience"}</div>
            <h2 className="exp-heading">Where I've worked.</h2>
          </div>
          <div className="exp-list">
            {EXPERIENCE.map((e, i) => (
              <div key={i} className="exp-item reveal" style={{ transitionDelay: `${i * 0.1}s` }}>
                <div className="exp-period">{e.period}</div>
                <div>
                  <div className="exp-role">{e.role}</div>
                  <div className="exp-company">{e.company}</div>
                  <div className="exp-desc">{e.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── EDUCATION ── */}
      <section id="education" style={{ position: 'relative', zIndex: 1 }}>
        <div className="edu-inner">
          <div className="reveal">
            <div className="section-label">{"// education"}</div>
            <h2 className="edu-heading">Where I studied.</h2>
          </div>
          <div className="edu-list">
            {EDUCATION.map((e, i) => (
              <div key={i} className="edu-item reveal" style={{ transitionDelay: `${i * 0.1}s` }}>
                <div className="edu-period">{e.period}</div>
                <div>
                  <div className="edu-degree">{e.degree}</div>
                  <div className="edu-school">{e.school}</div>
                  <div className="edu-desc">{e.desc}</div>
                  {e.honors.length > 0 && (
                    <div className="edu-honors">
                      {e.honors.map((h, j) => (
                        <span key={j} className={`edu-honor-badge ${h.style}`}>
                          {h.style === 'gold' ? '✦' : '◈'} {h.label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CONTACT ── */}
      <section style={{ position: 'relative', zIndex: 1 }}>
        <div id="contact">
          <div className="reveal">
            <div className="contact-eyebrow">{"// let's connect"}</div>
            <h2 className="contact-heading">
              Ready to<br /><span className="grad">build together?</span>
            </h2>
            <p className="contact-sub">
              Whether you have a role in mind, a product idea, or just want to say hello —
              my inbox is always open.
            </p>
            <div className="contact-links">
              <a href="mailto:jrbauti19@gmail.com" className="contact-link-pf">
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                  <rect x="1" y="3" width="13" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.3"/>
                  <path d="M1 4.5l6.5 5 6.5-5" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
                </svg>
                jrbauti19@gmail.com
              </a>
              <a
                href="https://www.linkedin.com/in/joshua-raphael-bautista-8a019a11b/"
                target="_blank"
                rel="noreferrer"
                className="contact-link-pf"
              >
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                  <rect x="1.5" y="1.5" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.3"/>
                  <path d="M4.5 6.5v4M4.5 4.5v.5M7 6.5v4M7 8.5c0-1 .5-2 2-2s2 1 2 2v2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                </svg>
                LinkedIn
              </a>
              <a
                href="https://github.com/jrbauti19"
                target="_blank"
                rel="noreferrer"
                className="contact-link-pf"
              >
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                  <path d="M7.5 1C3.91 1 1 3.91 1 7.5c0 2.87 1.86 5.3 4.44 6.16.32.06.44-.14.44-.3v-1.05c-1.8.39-2.18-.87-2.18-.87-.3-.75-.73-.95-.73-.95-.59-.41.05-.4.05-.4.65.05 1 .67 1 .67.59 1 1.54.71 1.91.54.06-.42.23-.71.42-.87-1.44-.16-2.95-.72-2.95-3.2 0-.71.25-1.28.67-1.74-.07-.16-.29-.82.06-1.72 0 0 .55-.18 1.79.67A6.2 6.2 0 017.5 5c.55 0 1.1.07 1.62.22 1.24-.85 1.78-.67 1.78-.67.36.9.13 1.56.07 1.72.42.46.67 1.03.67 1.74 0 2.49-1.52 3.04-2.96 3.2.23.2.44.6.44 1.2v1.78c0 .17.11.37.44.3C11.14 12.8 13 10.37 13 7.5 13 3.91 11.09 1 7.5 1z" fill="currentColor"/>
                </svg>
                GitHub
              </a>
              <a href="/resume" className="contact-link-pf">
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                  <path d="M2 2h11v9H2zM5 11v2M10 11v2M3.5 13.5h8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M5 6h5M5 8h3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                </svg>
                Resume
              </a>
            </div>
          </div>
        </div>
      </section>

      {openIdx !== null && (
        <CaseStudy
          project={PROJECTS[openIdx]}
          index={openIdx}
          total={PROJECTS.length}
          onClose={closeStudy}
          onNav={navStudy}
        />
      )}
    </>
  );
}

export default Home;
